import 'reflect-metadata';
import './sentry';
import {
  ScrollToTop,
  SnackbarProvider,
  ThemeProvider,
} from '@pflegenavi/web-components';
import type { FC, PropsWithChildren, JSX } from 'react';
import React, { StrictMode, useLayoutEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Router } from 'react-router-dom';
import type { BrowserHistory } from 'history';
import { createBrowserHistory } from 'history';

import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import App from './app/App';
import {
  AnalyticsApiProvider,
  CashListConfigurationProvider,
  CashManagementApiProvider,
  CashManagementPhoenixApiProvider,
  EmployeesPhoenixApiProvider,
  ExpensePhoenixApiProvider,
  ImageApiProvider,
  NursingHomePhoenixApiProvider,
  PaymentApiProvider,
  PaymentPhoenixApiProvider,
  PliantPhoenixApiProvider,
  ProfileTypeContext,
  ReceiptBatchPhoenixApiProvider,
  ReceiptsPhoenixApiProvider,
  RecurringItemPhoenixApiProvider,
  ReportingApiProvider,
  ReportingPhoenixApiProvider,
  ResidentApiProvider,
  ResidentPhoenixApiProvider,
  ServiceProviderApiProvider,
  ServiceProviderPhoenixApiProvider,
  SettlementPhoenixApiProvider,
  TransactionApiProvider,
  TransactionPhoenixApiProvider,
  useGetPaymentProcessor,
  UserProfileApiProvider,
  useUserProfileWithContext,
  UserProfilePhoenixApiProvider,
} from '@pflegenavi/frontend/api-nursing-home';
import { TenantContextProvider } from '@pflegenavi/frontend/tenant';
import { initI18N, LocaleProvider } from '@pflegenavi/frontend/localization';
import {
  localeStorage,
  LocalizationProvider,
  ThemeLocalization,
} from '@pflegenavi/frontend/localization-web-app';
import {
  AuthenticationProvider,
  useAuthenticationInternal,
} from '@pflegenavi/frontend/authentication';
import {
  PflegenaviAuthenticationProvider,
  shouldUseNewLogin,
  useAuthentication,
} from '@pflegenavi/shared-frontend/authentication';
import { setSentryAuthContext, setSentryApiUrl } from './sentry';
import ErrorBoundary from './app/components/ErrorBoundry';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { login } from './keycloak';
import { ExtensionProvider } from '@pflegenavi/frontend/layout';
import { useCashManagementNavConfig } from '@pflegenavi/frontend/pages/cash-management';
import { useReportingNavConfig } from '@pflegenavi/frontend/pages/reporting';
import { useRecurringItemsNavConfig } from '@pflegenavi/frontend/pages/residents';
import { useServiceProviderNavConfig } from '@pflegenavi/frontend/pages/service-provider';
import * as Sentry from '@sentry/react';
import { EmployeeModeProvider } from '@pflegenavi/shared-frontend/employee-mode';

// this is a virtual font file from vite pluging see: https://github.com/stafyniaksacha/vite-plugin-fonts
import 'unfonts.css';
import { useNursingHomeNavConfig } from '@pflegenavi/frontend/pages/nursing-home';
import {
  useNursingHomeAccountingNavConfig,
  useExpenseManagementNavConfig,
  useBudgetsNavConfig,
} from '@pflegenavi/frontend/pages/accountingPayments';
import {
  RedirectUrlProvider,
  ScopeDataSync,
  ScopeProvider,
  useNursingHomeContext,
  useCanAccessRoute,
} from '@pflegenavi/frontend/nursing-home-context';
import {
  FeatureFlagDevProvider,
  useFeatureFlagEnabled,
} from '@pflegenavi/frontend/feature-flags';
import { EmptyTrackingProvider } from '@pflegenavi/frontend/tracking';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { ClientStoredResourcesProvider } from '@pflegenavi/frontend/client-stored-resources-context';
import { FeatureFlag } from '@pflegenavi/shared/api';
import { PhoenixProvider } from '@pflegenavi/shared-frontend/phoenix';
import { getWebWelcomeToken } from '@pflegenavi/shared/utils';
import { getPhoenixApiBaseUrl } from '@pflegenavi/shared-frontend/platform';

window.global ||= window;

function BrowserRouter({ children }: PropsWithChildren): JSX.Element {
  const ref = useRef<BrowserHistory | undefined>(undefined);

  if (!ref.current) {
    ref.current = createBrowserHistory({
      window,
    });
    // @ts-expect-error // reactHistory is untyped
    window.global.reactHistory = ref.current;
  }

  const history = ref.current;
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });
  useLayoutEffect(() => history.listen(setState), [history]);
  return (
    <Router
      location={state.location}
      navigationType={state.action}
      navigator={history}
    >
      {children}
    </Router>
  );
}

// @ts-expect-error // pflegenaviApplication is untyped
window.global.pflegenaviApplication = 'nursing-home-web';
// @ts-expect-error // pflegenaviApplicationVersion is untyped
window.global.pflegenaviApplicationVersion =
  // @ts-expect-error // SENTRY_RELEASE is untyped
  typeof SENTRY_RELEASE !== 'undefined' ? SENTRY_RELEASE.id : 'local';
await initI18N(localeStorage);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 0, //1000 * 60 * 5, // 5 minutes
    },
    mutations: {
      onError: (error, variables, context) => {
        Sentry.captureException(error);
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(error);
    },
  }),
});

const NavExtension: FC<PropsWithChildren> = ({ children }) => {
  // The actual nav extension will only be fetched when we fetched the user profile once. Otherwise every feature flag check will fetch the employee profile endpoint
  // TODO: We need to refactor this.

  const { isPending } = useUserProfileWithContext({});

  if (isPending) {
    return children;
  }

  return <NavExtensionInner>{children}</NavExtensionInner>;
};

const NavExtensionInner: FC<PropsWithChildren> = ({ children }) => {
  const canAccessRecurringItems = useCanAccessRoute(
    nhAppMainPages.RECURRING_ITEMS_V2
  );
  const canAccessServiceProvider = useCanAccessRoute(
    nhAppMainPages.SERVICE_PROVIDER
  );
  const canAccessAccounting = useCanAccessRoute(
    nhAppMainPages.NURSING_HOME_ACCOUNTING
  );
  const canAccessNursingHome = useCanAccessRoute(nhAppMainPages.NURSING_HOME);

  const includeCashManagement = !useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  );
  // Expense-management-only tenants hide non-expense-management nav areas. See ENG-2056.
  const receiptManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ReceiptManagement
  );
  const paymentProcessor = useGetPaymentProcessor();
  const nursingHomeNavConfig = useNursingHomeNavConfig();
  const recurringItemsNavConfig = useRecurringItemsNavConfig();
  const cashManagementNavConfig = useCashManagementNavConfig();
  const nursingHomeAccountingNavConfig = useNursingHomeAccountingNavConfig();
  const reportingNavConfig = useReportingNavConfig();
  const serviceProviderNavConfig = useServiceProviderNavConfig();
  const expenseManagementNavConfig = useExpenseManagementNavConfig();
  const budgetsNavConfig = useBudgetsNavConfig();
  return (
    <ExtensionProvider
      value={[
        expenseManagementNavConfig,
        budgetsNavConfig,
        ...(canAccessRecurringItems && receiptManagementEnabled
          ? [recurringItemsNavConfig]
          : []),
        ...(canAccessServiceProvider && receiptManagementEnabled
          ? [serviceProviderNavConfig]
          : []),
        ...(canAccessAccounting ? [nursingHomeAccountingNavConfig] : []),
        ...(includeCashManagement && receiptManagementEnabled
          ? [cashManagementNavConfig]
          : []),
        ...(paymentProcessor === 'stripe' && receiptManagementEnabled
          ? [reportingNavConfig]
          : []),
        ...(canAccessNursingHome ? [nursingHomeNavConfig] : []),
      ]}
    >
      {children}
    </ExtensionProvider>
  );
};

const container = document.getElementById('root');

const root = createRoot(container!);

// Component to set global auth context for Sentry feedback
const SetSentryAuth: FC<PropsWithChildren> = ({ children }) => {
  const authContext = useAuthenticationInternal();
  React.useLayoutEffect(() => {
    setSentryAuthContext(authContext);
  }, [authContext]);
  return <>{children}</>;
};

const AuthWrapper: FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuthentication(true);

  return (
    <AuthenticationProvider
      keycloak={auth}
      login={login}
      redirectUri={`${window.location.origin}/${nhAppMainPages.DASHBOARD}`}
      isNewAuth={Boolean(auth)}
    >
      <SetSentryAuth>{children}</SetSentryAuth>
    </AuthenticationProvider>
  );
};

function redirectIfNecessary(url: string) {
  const hostname = new URL(url).hostname;

  if (hostname !== window.location.hostname) {
    window.location.replace(url);
    return 'redirected';
  }
  return undefined;
}

const getQueryParam = (param: string) => {
  return new URLSearchParams(window.location.search).get(param);
};

const apiUrl = getPhoenixApiBaseUrl(undefined);
setSentryApiUrl(apiUrl);

root.render(
  <ClientStoredResourcesProvider storage={localStorage}>
    <PflegenaviAuthenticationProvider
      storage={localStorage}
      getWelcomeToken={getWebWelcomeToken}
      shouldUseNewLogin={shouldUseNewLogin()}
      apiUrl={apiUrl}
      redirectIfNecessary={redirectIfNecessary}
      onLogout={() => queryClient.clear()}
    >
      <AuthWrapper>
        <StrictMode>
          <BrowserRouter>
            <SnackbarProvider>
              <QueryClientProvider client={queryClient}>
                <TenantContextProvider>
                  <ScopeProvider getQueryParam={getQueryParam}>
                    <RedirectUrlProvider>
                      <ServiceProviderApiProvider>
                        <ServiceProviderPhoenixApiProvider>
                          <TransactionApiProvider>
                            <TransactionPhoenixApiProvider>
                              <ResidentApiProvider>
                                <ResidentPhoenixApiProvider>
                                  <ImageApiProvider>
                                    <AnalyticsApiProvider>
                                      <PliantPhoenixApiProvider>
                                        <ProfileTypeContext.Provider value="employee">
                                          <EmployeesPhoenixApiProvider>
                                            <UserProfileApiProvider>
                                              <UserProfilePhoenixApiProvider>
                                                <CashManagementApiProvider>
                                                  <CashManagementPhoenixApiProvider>
                                                    <SettlementPhoenixApiProvider>
                                                      <CashListConfigurationProvider
                                                        useNursingHomeContext={
                                                          useNursingHomeContext
                                                        }
                                                      >
                                                        <ReportingApiProvider>
                                                          <ReportingPhoenixApiProvider>
                                                            <ReceiptBatchPhoenixApiProvider>
                                                              <RecurringItemPhoenixApiProvider>
                                                                <FeatureFlagDevProvider>
                                                                  <ReceiptsPhoenixApiProvider>
                                                                    <NursingHomePhoenixApiProvider>
                                                                      <ExpensePhoenixApiProvider>
                                                                        <PhoenixProvider>
                                                                          <NavExtension>
                                                                            <ThemeProvider>
                                                                              <LocaleProvider
                                                                                localeStorage={
                                                                                  localeStorage
                                                                                }
                                                                              >
                                                                                <LocalizationProvider>
                                                                                  <ThemeLocalization>
                                                                                    <PaymentApiProvider>
                                                                                      <PaymentPhoenixApiProvider>
                                                                                        <DndProvider
                                                                                          backend={
                                                                                            HTML5Backend
                                                                                          }
                                                                                        >
                                                                                          <ScrollToTop />
                                                                                          <ScopeDataSync />
                                                                                          <ErrorBoundary>
                                                                                            <EmptyTrackingProvider>
                                                                                              <EmployeeModeProvider>
                                                                                                <App />
                                                                                              </EmployeeModeProvider>
                                                                                            </EmptyTrackingProvider>
                                                                                          </ErrorBoundary>
                                                                                        </DndProvider>
                                                                                      </PaymentPhoenixApiProvider>
                                                                                    </PaymentApiProvider>
                                                                                  </ThemeLocalization>
                                                                                </LocalizationProvider>
                                                                              </LocaleProvider>
                                                                            </ThemeProvider>
                                                                          </NavExtension>
                                                                        </PhoenixProvider>
                                                                      </ExpensePhoenixApiProvider>
                                                                    </NursingHomePhoenixApiProvider>
                                                                  </ReceiptsPhoenixApiProvider>
                                                                </FeatureFlagDevProvider>
                                                              </RecurringItemPhoenixApiProvider>
                                                            </ReceiptBatchPhoenixApiProvider>
                                                          </ReportingPhoenixApiProvider>
                                                        </ReportingApiProvider>
                                                      </CashListConfigurationProvider>
                                                    </SettlementPhoenixApiProvider>
                                                  </CashManagementPhoenixApiProvider>
                                                </CashManagementApiProvider>
                                              </UserProfilePhoenixApiProvider>
                                            </UserProfileApiProvider>
                                          </EmployeesPhoenixApiProvider>
                                        </ProfileTypeContext.Provider>
                                      </PliantPhoenixApiProvider>
                                      <ReactQueryDevtools
                                        initialIsOpen={false}
                                      />
                                    </AnalyticsApiProvider>
                                  </ImageApiProvider>
                                </ResidentPhoenixApiProvider>
                              </ResidentApiProvider>
                            </TransactionPhoenixApiProvider>
                          </TransactionApiProvider>
                        </ServiceProviderPhoenixApiProvider>
                      </ServiceProviderApiProvider>
                    </RedirectUrlProvider>
                  </ScopeProvider>
                </TenantContextProvider>
              </QueryClientProvider>
            </SnackbarProvider>
          </BrowserRouter>
        </StrictMode>
      </AuthWrapper>
    </PflegenaviAuthenticationProvider>
  </ClientStoredResourcesProvider>
);
